import {
    AfterViewInit,
    Component,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    viewChild,
} from "@angular/core";
import { RentVsBuyService } from "../../services/rent-vs-buy.service"; // Chart.js is loaded globally via CDN script tag in index.html

// Chart.js is loaded globally via CDN script tag in index.html
declare const Chart: any;

@Component({
    selector: "hs-rvb-chart-card",
    standalone: true,
    template: `
        <div class="chart-card">
            <div class="chart-card__header">
                <div>
                    <div class="chart-card__title">{{ title() }}</div>
                    <div class="chart-card__sub">{{ subtitle() }}</div>
                </div>
                <div class="chart-card__legend">
                    <div class="chart-card__legend-item">
                        <div
                            class="chart-card__legend-sq chart-card__legend-sq--buy"
                        ></div>
                        {{ buyLabel() }}
                    </div>
                    <div class="chart-card__legend-item">
                        <div
                            class="chart-card__legend-sq chart-card__legend-sq--rent"
                        ></div>
                        {{ rentLabel() }}
                    </div>
                </div>
            </div>
            <div class="chart-card__canvas-wrap">
                <canvas
                    #canvas
                    [attr.role]="'img'"
                    [attr.aria-label]="title()"
                    >{{ title() }}</canvas
                >
            </div>
        </div>
    `,
    styleUrl: "./rvb-chart-card.component.scss",
})
export class RvbChartCardComponent implements AfterViewInit, OnDestroy {
    readonly title = input.required<string>();
    readonly subtitle = input.required<string>();
    readonly chartId = input.required<string>();
    readonly buyLabel = input.required<string>();
    readonly rentLabel = input.required<string>();
    readonly buyData = input.required<number[]>();
    readonly rentData = input.required<number[]>();
    readonly labels = input.required<string[]>();
    readonly allowNegative = input<boolean>(true);

    private readonly canvasRef =
        viewChild.required<ElementRef<HTMLCanvasElement>>("canvas");
    private chart: any = null;
    protected readonly svc = inject(RentVsBuyService);

    private oldBuyData: Array<number> = [];
    private oldRentData: Array<number> = [];
    private oldLabsData: Array<string> = [];

    constructor() {
        // Re-render whenever any input signal changes
        effect(() => {
            // Read all reactive inputs to register dependencies
            const buy = this.buyData();
            const rent = this.rentData();
            const labs = this.labels();

            const buyNotSame = buy.find((value, index) => {
                return value !== this.oldBuyData[index];
            });
            const rentNotSame = rent.find((value, index) => {
                return value !== this.oldRentData[index];
            });
            const labsNotSame = labs.find((value, index) => {
                return value !== this.oldLabsData[index];
            });
            if (buyNotSame || rentNotSame || labsNotSame) {
                this.oldBuyData = buy;
                this.oldRentData = rent;
                this.oldLabsData = labs;
                // Defer so canvas is guaranteed to exist
                setTimeout(() => this.render(buy, rent, labs), 0);
            }
        });
    }

    ngAfterViewInit(): void {
        this.render(this.buyData(), this.rentData(), this.labels());
    }

    ngOnDestroy(): void {
        this.chart?.destroy();
    }

    private fmt(v: number): string {
        const abs = Math.round(Math.abs(v) / 1000);
        return (v < 0 ? "-" : "") + "$" + abs + "k";
    }

    private render(buy: number[], rent: number[], labs: string[]): void {
        const canvas = this.canvasRef()?.nativeElement;
        if (!canvas || typeof Chart === "undefined") return;

        this.chart?.destroy();

        const pts = labs.length > 15 ? 0 : 4;

        this.chart = new Chart(canvas, {
            type: "line",
            data: {
                labels: labs,
                datasets: [
                    {
                        label: this.buyLabel(),
                        data: buy,
                        borderColor: "#2a78d6",
                        backgroundColor: "rgb(42 120 214 / 0.15)",
                        borderWidth: 2.5,
                        tension: 0.35,
                        pointRadius: pts,
                        pointBackgroundColor: "#2a78d6",
                        fill: true,
                    },
                    {
                        label: this.rentLabel(),
                        data: rent,
                        borderColor: "#1baf7a",
                        backgroundColor: "rgb(27 175 122 / 0.15)",
                        borderWidth: 2.5,
                        tension: 0.35,
                        pointRadius: pts,
                        pointBackgroundColor: "#1baf7a",
                        fill: true,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        callbacks: {
                            label: (c: any) =>
                                `${c.dataset.label}: ${this.fmt(c.raw)}`,
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { color: "rgb(0 0 0)" },
                        ticks: {
                            color: "#5a7a99",
                            font: { size: 11, family: "Red Hat Text" },
                        },
                    },
                    y: {
                        grid: { color: "rgb(0 0 0)" },
                        ticks: {
                            color: "#5a7a99",
                            font: { size: 11, family: "Red Hat Text" },
                            callback: (v: number) => this.fmt(v),
                        },
                    },
                },
            },
        });
    }
}
